"use client";

import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "../../../../components/ui/card";
import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from "next/navigation";
import { Image } from "@imagekit/next";

const HeroSection = () => {
  const router = useRouter();
  const [currentImage, setCurrentImage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);

  const folder = process.env.NEXT_PUBLIC_IMAGEKIT_FOLDER || "";

  const imageFiles = [
    "outsitting.JPG",
    "Nainital.JPG",
    "closedsitting.webp",
    "guitaract.JPG",
  ];

  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        setCurrentImage((prev) => (prev + 1) % imageFiles.length);
      }, 3000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPaused, imageFiles.length]);

  const handleDotClick = (index) => {
    setCurrentImage(index);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
  };

  const cardVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 1.2, delay: 1.5, ease: "easeOut" },
    },
  };

  const textVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const imageVariants = {
    enter: { opacity: 0 },
    center: {
      opacity: 1,
      transition: { duration: 0.8, ease: "easeInOut" },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.8 },
    },
  };

  const lidVariants = {
    closed: { 
      rotateX: -95,
      opacity: 0 
    },
    open: { 
      rotateX: 0,
      opacity: 1,
      transition: { 
        duration: 2.5, 
        ease: [0.25, 1, 0.5, 1], // Custom cubic-bezier for a smooth stop
        delay: 0.3
      }
    }
  };

  return (
    <div className="relative w-full min-h-[600px] flex items-center bg-white overflow-hidden">
      <div className="w-[90%] mx-auto py-6 sm:py-8 md:py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 items-center">
          
          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
          >
            <Card className="rounded-[20px] border-none bg-white shadow-none">
              <CardContent className="flex flex-col items-start gap-4 sm:gap-6 p-4 sm:p-6">
                <motion.div className="flex flex-col items-start gap-3 w-full">
                  <motion.h1
                    className="font-bricolage font-bold text-[#1d1d1d] text-2xl sm:text-3xl md:text-[56px] tracking-[-0.75px] md:tracking-[-1.2px] leading-tight sm:leading-[40px] md:leading-[64px]"
                    variants={textVariants}
                  >
                    What if a trip could change how you think, not just where you go?
                  </motion.h1>

                  <motion.h4
                    className="font-jakarta font-semibold text-[#1d1d1d] text-xs sm:text-sm md:text-lg leading-5 sm:leading-6 md:leading-8 max-w-lg"
                    variants={textVariants}
                  >
                    Join a curated experience led by inspiring mentors - where
                    learning meets leisure, strangers become community, and
                    everyone leaves with more than memories.
                  </motion.h4>

                  <motion.h5
                    className="font-jakarta font-semibold text-[#1d1d1d] text-xs leading-5 sm:leading-6 md:leading-8 max-w-lg"
                    variants={textVariants}
                  >
                    For entrepreneurs, creators, students, professionals, solo
                    explorers and anyone seeking clarity, growth, and connection.
                  </motion.h5>
                </motion.div>

                <motion.div
                  variants={textVariants}
                  className="flex flex-col md:flex-row justify-around gap-4 mt-2 sm:mt-0"
                >
                  <button
                    onClick={() => {
                      const section = document.getElementById("travelPackagesSection");
                      section?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="cursor-pointer relative bg-[#063258] py-2 rounded-full min-w-[8.5rem] min-h-[2.92rem] group max-w-full flex items-center justify-start hover:bg-[#FFB619] transition-all duration-[0.8s] ease-[cubic-bezier(0.510,0.026,0.368,1.016)] shadow-[inset_1px_2px_5px_#00000080]"
                  >
                    <div className="absolute flex px-1 py-0.5 justify-start items-center inset-0">
                      <div className="w-[0%] group-hover:w-full transition-all duration-[1s] ease-[cubic-bezier(0.510,0.026,0.368,1.016)]" />
                      <div className="rounded-full shrink-0 flex justify-center items-center shadow-[inset_1px_-1px_3px_0_black] h-full aspect-square bg-[#FFB619] transition-all duration-[1s] ease-[cubic-bezier(0.510,0.026,0.368,1.016)] group-hover:bg-black">
                        <div className="size-[0.8rem] text-black group-hover:text-white group-hover:-rotate-45 transition-all duration-[1s] ease-[cubic-bezier(0.510,0.026,0.368,1.016)]">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16" height="100%" width="100%">
                            <path fill="currentColor" d="M12.175 9H0V7H12.175L6.575 1.4L8 0L16 8L8 16L6.575 14.6L12.175 9Z"></path>
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div className="pl-[3.4rem] pr-[1.1rem] font-bricolage font-semibold group-hover:pl-[1.1rem] group-hover:pr-[3.4rem] transition-all duration-[1s] ease-[cubic-bezier(0.510,0.026,0.368,1.016)] group-hover:text-black text-white">
                      Explore Experiences
                    </div>
                  </button>
                  <button className="cursor-pointer relative bg-[#FFB619] py-2 rounded-full min-w-[8.5rem] min-h-[2.92rem] group max-w-full flex items-center justify-start hover:bg-[#063258] transition-all duration-[0.8s] ease-[cubic-bezier(0.510,0.026,0.368,1.016)] shadow-[inset_1px_2px_5px_#00000080]">
                    <a href="https://chat.whatsapp.com/C3SkpzYbUoj037GX3cPpsx" target="_blank" rel="noopener noreferrer" className="flex items-center w-full h-full">
                      <div className="absolute flex px-1 py-0.5 justify-start items-center inset-0">
                        <div className="w-[0%] group-hover:w-full transition-all duration-[1s] ease-[cubic-bezier(0.510,0.026,0.368,1.016)]" />
                        <div className="rounded-full shrink-0 flex justify-center items-center shadow-[inset_1px_-1px_3px_0_black] h-full aspect-square bg-[#063258] transition-all duration-[1s] ease-[cubic-bezier(0.510,0.026,0.368,1.016)] group-hover:bg-black">
                          <div className="size-[0.8rem] text-black group-hover:text-white group-hover:-rotate-45 transition-all duration-[1s] ease-[cubic-bezier(0.510,0.026,0.368,1.016)]">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16" height="100%" width="100%">
                              <path fill="white" d="M12.175 9H0V7H12.175L6.575 1.4L8 0L16 8L8 16L6.575 14.6L12.175 9Z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="pl-[3.4rem] pr-[1.1rem] font-bricolage font-semibold group-hover:pl-[1.1rem] group-hover:pr-[3.4rem] transition-all duration-[1s] ease-[cubic-bezier(0.510,0.026,0.368,1.016)] group-hover:text-white text-black">
                        Join Community
                      </div>
                    </a>
                  </button>
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>

          <div className="relative perspective-2000 py-12 flex justify-center items-center">
            <motion.div 
              className="absolute w-[80%] h-[20%] bottom-8 bg-black/40 blur-[40px] rounded-[100%] -z-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 2, delay: 0.5 }}
            />

            <motion.div
              className="relative w-full max-w-[720px] preserve-3d origin-bottom"
              initial={{ rotateY: -15, rotateX: 5 }}
              animate={{ rotateY: -15, rotateX: 5 }}
              whileHover={{ rotateY: 0, rotateX: 0, scale: 1.02 }}
              transition={{ duration: 1.2 }}
            >
              <motion.div
                variants={lidVariants}
                initial="closed"
                animate="open"
                className="relative bg-[#222] rounded-[24px] p-[10px] pb-[4px] border-[0.5px] border-white/20 shadow-2xl origin-bottom"
              >
                <div className="relative bg-[#0a0a0a] rounded-[18px] p-[8px] overflow-hidden">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-5 bg-[#0a0a0a] rounded-b-2xl z-20 flex justify-center items-center">
                    <div className="w-1 h-1 rounded-full bg-white/10" />
                  </div>

                  <div className="relative aspect-[16/10] w-full overflow-hidden rounded-[10px] bg-black">
                    <AnimatePresence initial={false} mode="wait">
                      <motion.div
                        key={currentImage}
                        variants={imageVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        className="w-full h-full absolute"
                      >
                        <Image
                          src={`${folder}/${imageFiles[currentImage]}`}
                          alt="Experience"
                          fill
                          className="object-cover"
                          priority
                          transformation={[{ width: 1600, quality: 100 }]}
                        />
                        <div className="absolute inset-0 bg-gradient-to-tr from-white/10 via-transparent to-transparent pointer-events-none" />
                      </motion.div>
                    </AnimatePresence>

                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3 z-30">
                      {imageFiles.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => handleDotClick(index)}
                          className={`h-1.5 rounded-full transition-all duration-500 ${
                            currentImage === index ? "w-8 bg-white" : "w-1.5 bg-white/40"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>

              <div className="relative h-4 w-[104%] -left-[2%] bg-[#1a1a1a] rounded-b-[20px] border-t border-white/10 shadow-2xl">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-2 bg-black/40 rounded-b-lg" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .perspective-2000 { perspective: 2000px; }
        .preserve-3d { transform-style: preserve-3d; }
        .origin-bottom { transform-origin: bottom; }
      `}</style>
    </div>
  );
};

export default HeroSection;
